// Das Gedächtnis (docs/gedaechtnis.md): Chats, Merker, Doku und Commits
// durchsuchen. Hier die reinen Regeln (geprüft in gedaechtnis.test.mjs);
// Stand und Suche holt lib/gedaechtnis-summary.svelte.ts.

import type { LageTeil } from "../lage";

export const ARTEN = ["wechsel", "zusammenfassung", "merker", "doku", "commit", "verdichtung"] as const;
export type Art = (typeof ARTEN)[number];

/** Stand ohne Inhalte (GET /v2-api/gedaechtnis, WS „gedaechtnis"). */
export interface GedaechtnisStand {
  bereit: boolean;
  eintraege: Record<string, number>;
  teile: number;
  vektoren: number;
  offen: number;
  groesse: number;
  modell: string;
  /** "" ruht, "laeuft", "wartet" (Inferenzdienst nicht erreichbar) */
  einbetten: string;
  letzter_durchgang?: string;
  dauer_ms: number;
  fehler?: string;
  chats: number;
  sitzungen: number;
  /** "" ruht, "laeuft", "ohne_zugang", "fehler", "aus" */
  zusammenfassen?: string;
  /** Lesen und Schreiben, ohne Inhalt (internal/gedaechtnis/zugriffe.go) */
  zugriffe?: ZugriffStand;
}

export interface Zugriff {
  id: number;
  zeit: string;
  art: "lesen" | "schreiben";
  /** suche | lesen | erinnern | eingelesen | vektoren | zusammenfassung | neu */
  was: string;
  /** chat | seite | dienst */
  wer?: string;
  chat?: string;
  anzahl: number;
  /** ohne Inhalt: „3 Chat-Wechsel · 1 Merker", die Art des Gelesenen */
  kurz?: string;
  /** Inhalt (Frage, Titel) -- nur hinter der PIN */
  text?: string;
  dauer_ms?: number;
  /** Teile, deren Bedeutung zu diesem Vorgang gerechnet wurde (seit
   *  21.10.2026 beim Einlesen statt als eigene Zeile) */
  vektoren?: number;
}

export interface ZugriffStand {
  /** Zugriffe der letzten Stunde */
  lesen: number;
  schreiben: number;
  /** dieselbe Stunde nach Vorgang (suche, erinnern, eingelesen …) */
  lesen_je?: Record<string, number>;
  schreiben_je?: Record<string, number>;
  /** Treffer von Suche und Erinnern, eingelesene Einträge, Teile mit Bedeutung */
  treffer?: number;
  eingelesen?: number;
  vektoren?: number;
  letzte: Zugriff[];
  /** alle Zugriffe, die das Gedächtnis noch führt (30 Tage) */
  gesamt?: number;
  letzter_lesen?: string;
  letzter_schreiben?: string;
}

export interface ChatKurz {
  id: string;
  titel: string;
  repo: string;
  /** steht noch in der Brücke (sonst weggeräumt: kein Sprung) */
  da: boolean;
}

export interface Treffer {
  id: number;
  schluessel: string;
  art: Art;
  titel: string;
  repo?: string;
  chat?: ChatKurz;
  sitzung?: string;
  zeit?: string;
  bezug?: string;
  /** Fundstellen zwischen ‹ und › */
  ausschnitt: string;
  wertung: number;
  wege: ("text" | "bedeutung")[];
  naehe?: number;
}

export interface Ergebnis {
  frage: string;
  treffer: Treffer[];
  ohne_bedeutung?: boolean;
  dauer_ms: number;
}

export interface Eintrag {
  id: number;
  schluessel: string;
  art: Art;
  repo?: string;
  sitzung?: string;
  chat_id?: string;
  chat?: ChatKurz;
  zeit?: string;
  titel: string;
  text: string;
  schritte?: string;
  bezug?: string;
  reihe: number;
}

export interface Lesen {
  eintrag: Eintrag;
  davor?: Eintrag[];
  danach?: Eintrag[];
}

/** Gesamtzahl der Einträge (Zähl-Pille). */
export function summe(st: GedaechtnisStand | undefined): number {
  if (!st) return 0;
  return Object.values(st.eintraege ?? {}).reduce((a, b) => a + b, 0);
}

/** Anteil der Teile mit Vektor, 0..1. */
export function anteilBedeutung(st: GedaechtnisStand): number {
  const alle = st.vektoren + st.offen;
  return alle > 0 ? st.vektoren / alle : 1;
}

/** Lage-Satz des Index (docs/gestaltung.md, Regel 3): Ausnahme vorn. */
export function indexLage(st: GedaechtnisStand | undefined, worte: {
  laedt: string;
  nichtBereit: string;
  fehler: string;
  wartet: string;
  bedeutung: (prozent: string) => string;
  bereit: string;
  gelesen: (wann: string) => string;
}, wann: (iso: string) => string): LageTeil[] {
  if (!st) return [{ text: worte.laedt }];
  if (!st.bereit) return [{ text: worte.nichtBereit }];
  const teile: LageTeil[] = [];
  if (st.fehler) teile.push({ text: worte.fehler, ton: "danger" });
  if (st.einbetten === "wartet") teile.push({ text: worte.wartet, ton: "warn" });
  if (st.offen > 0) {
    const p = Math.floor(anteilBedeutung(st) * 100);
    teile.push({ text: worte.bedeutung(`${p} %`), ton: st.einbetten === "laeuft" ? "accent" : undefined });
  }
  if (!teile.length) teile.push({ text: worte.bereit, ton: "ok" });
  if (st.letzter_durchgang) teile.push({ text: worte.gelesen(wann(st.letzter_durchgang)) });
  return teile;
}

/** Der Ausschnitt in Stücke: markiert = Fundstelle (‹…›). */
export function ausschnittTeile(s: string): { text: string; mark: boolean }[] {
  const out: { text: string; mark: boolean }[] = [];
  let rest = s ?? "";
  while (rest) {
    const a = rest.indexOf("‹");
    if (a < 0) {
      out.push({ text: rest, mark: false });
      break;
    }
    const b = rest.indexOf("›", a + 1);
    if (b < 0) {
      out.push({ text: rest.replace("‹", ""), mark: false });
      break;
    }
    if (a > 0) out.push({ text: rest.slice(0, a), mark: false });
    out.push({ text: rest.slice(a + 1, b), mark: true });
    rest = rest.slice(b + 1);
  }
  return out.filter((x) => x.text !== "");
}

export const FORGEJO = "https://forgejo.adwig.me/tim/";

/** Wohin ein Treffer führt: in den Chat an der Stelle, auf die
 *  Diff-Seite des Commits, zur Datei auf Forgejo. null = nirgendwohin
 *  (Chat weggeräumt, Sitzung ohne Homelab-Chat). */
export function zielFuer(t: { art: Art; chat?: ChatKurz; zeit?: string; bezug?: string }): { pfad: string; extern: boolean } | null {
  switch (t.art) {
    case "wechsel":
    case "verdichtung":
      if (t.chat?.da && t.chat.id) {
        return { pfad: `/claude/${encodeURIComponent(t.chat.id)}` + (t.zeit ? `?bei=${encodeURIComponent(t.zeit)}` : ""), extern: false };
      }
      return null;
    case "zusammenfassung":
      return t.chat?.da && t.chat.id ? { pfad: `/claude/${encodeURIComponent(t.chat.id)}`, extern: false } : null;
    case "commit": {
      const [repo, hash] = (t.bezug ?? "").split(":");
      return repo && hash ? { pfad: `/claude/aenderung/${encodeURIComponent(repo)}/${encodeURIComponent(hash)}`, extern: false } : null;
    }
    case "doku": {
      const b = t.bezug ?? "";
      const i = b.indexOf("/");
      if (i <= 0) return null;
      const [datei, anker] = b.slice(i + 1).split("#");
      return { pfad: `${FORGEJO}${b.slice(0, i)}/src/branch/main/${datei}${anker ? "#" + anker : ""}`, extern: true };
    }
    case "merker":
      return t.bezug ? { pfad: `${FORGEJO}claude-gedaechtnis/src/branch/main/${t.bezug}`, extern: true } : null;
  }
  return null;
}

/** Woher ein Treffer kommt, kurz: Chat-Titel, sonst Repo. */
export function herkunft(t: { art: Art; chat?: ChatKurz; repo?: string }, ohneChat: string): string {
  if (t.art === "wechsel" || t.art === "verdichtung" || t.art === "zusammenfassung") {
    if (t.chat?.titel) return t.chat.titel;
    if (t.art === "wechsel") return ohneChat;
  }
  return t.repo ?? "";
}

/** Der Wechsel als Tim-/Claude-Teile für die Anzeige im Detail. */
export function wechselTeile(text: string): { wer: "tim" | "claude"; text: string }[] {
  const s = text ?? "";
  if (!s.startsWith("Tim: ")) return [{ wer: "claude", text: s }];
  const i = s.indexOf("\n\nClaude: ");
  if (i < 0) return [{ wer: "tim", text: s.slice(5) }];
  return [
    { wer: "tim", text: s.slice(5, i) },
    { wer: "claude", text: s.slice(i + 10) },
  ];
}

/** Suchadresse der Seite (aus dem Baum, aus ⌘K). */
export function suchPfad(frage: string): string {
  const f = frage.trim();
  return "/claude/gedaechtnis" + (f ? "?frage=" + encodeURIComponent(f) : "");
}

/** Was der Zusammenfasser gerade tut, als Teil eines Lage-Satzes. */
export function zusammenfassenLage(st: GedaechtnisStand | undefined, worte: { laeuft: string; ohneZugang: string; fehler: string }): LageTeil[] {
  switch (st?.zusammenfassen) {
    case "laeuft":
      return [{ text: worte.laeuft, ton: "accent" }];
    case "ohne_zugang":
      return [{ text: worte.ohneZugang }];
    case "fehler":
      return [{ text: worte.fehler, ton: "warn" }];
  }
  return [];
}

/** Was ein Zugriff tat, als Wort (Baum, Tabelle). */
export function zugriffWort(z: Pick<Zugriff, "was">): string {
  switch (z.was) {
    case "suche":
      return "gesucht";
    case "lesen":
      return "gelesen";
    case "erinnern":
      return "erinnert";
    case "eingelesen":
      return "eingelesen";
    case "vektoren":
      return "Bedeutung gerechnet";
    case "zusammenfassung":
      return "zusammengefasst";
    case "neu":
      return "neu eingelesen";
  }
  return z.was;
}

/** Wer zugegriffen hat: der Chat beim Namen, „Seite" für Tim, sonst nichts
 *  (der Dienst selbst). */
export function zugriffWer(z: Pick<Zugriff, "wer" | "chat">, chatTitel: (id: string) => string | undefined): string {
  if (z.chat) return chatTitel(z.chat) ?? "Chat";
  if (z.wer === "seite") return "Seite";
  if (z.wer === "chat") return "Chat";
  return "";
}

/** Eine Zeile im Baum, ohne Inhalt: „gesucht · Homelab · 3 Treffer". */
export function zugriffZeile(z: Zugriff, chatTitel: (id: string) => string | undefined): string {
  const teile = [zugriffWort(z)];
  const wer = zugriffWer(z, chatTitel);
  if (wer) teile.push(wer);
  switch (z.was) {
    case "suche":
    case "erinnern":
      teile.push(z.anzahl === 1 ? "1 Treffer" : `${z.anzahl} Treffer`);
      break;
    case "lesen":
      if (z.kurz) teile.push(z.kurz);
      break;
    case "eingelesen":
    case "neu": {
      const k = zugriffKurz(z);
      if (k) teile.push(k);
      break;
    }
    case "vektoren":
      teile.push(z.anzahl === 1 ? "1 Teil" : `${z.anzahl} Teile`);
      break;
  }
  return teile.join(" · ");
}

/** Was ein Schreib-Zugriff gebracht hat, ohne Inhalt: „2 Chat-Wechsel ·
 *  Bedeutung für 5 Teile". */
export function zugriffKurz(z: Pick<Zugriff, "was" | "kurz" | "vektoren">): string {
  const teile = z.kurz ? [z.kurz] : [];
  if (z.was !== "vektoren" && z.vektoren) teile.push(z.vektoren === 1 ? "Bedeutung für 1 Teil" : `Bedeutung für ${z.vektoren} Teile`);
  return teile.join(" · ");
}

const LESEN_WORT: Record<string, string> = {
  erinnern: "erinnert (bei jeder Nachricht in einem Chat)",
  suche: "gesucht",
  lesen: "ganz gelesen",
};
const SCHREIBEN_WORT: Record<string, string> = {
  eingelesen: "eingelesen",
  zusammenfassung: "zusammengefasst",
  neu: "neu aufgebaut",
  vektoren: "Bedeutung nachgerechnet",
};

function aufzaehlung(je: Record<string, number> | undefined, worte: Record<string, string>, extra: (was: string) => string = () => ""): string {
  const reihe = Object.keys(worte);
  return Object.entries(je ?? {})
    .filter(([, n]) => n > 0)
    .sort(([a], [b]) => (reihe.indexOf(a) + 1 || 99) - (reihe.indexOf(b) + 1 || 99))
    .map(([was, n]) => `${n}× ${worte[was] ?? was}${extra(was)}`)
    .join(", ");
}

/** Was die beiden Zahlen am Gedächtnis sagen (Tim 21.10.2026: „Was sagen
 *  die Zahlen genau?"): je ein Satz für Lesen und Schreiben der letzten
 *  Stunde, für Tooltip und Seite. */
export function zugriffeErklaerung(z: ZugriffStand | undefined): { lesen: string; schreiben: string } {
  if (!z) return { lesen: "", schreiben: "" };
  let lesen = "In der letzten Stunde nichts gelesen.";
  if (z.lesen > 0) {
    lesen = `${z.lesen}× gelesen in der letzten Stunde`;
    const auf = aufzaehlung(z.lesen_je, LESEN_WORT);
    if (auf) lesen += `: ${auf}`;
    if (z.treffer) lesen += ` – ${z.treffer} Treffer`;
  }
  let schreiben = "In der letzten Stunde nichts geschrieben.";
  if (z.schreiben > 0) {
    schreiben = `${z.schreiben}× geschrieben in der letzten Stunde`;
    const auf = aufzaehlung(z.schreiben_je, SCHREIBEN_WORT, (was) => (was === "eingelesen" && z.eingelesen ? ` (${z.eingelesen} ${z.eingelesen === 1 ? "Eintrag" : "Einträge"})` : ""));
    if (auf) schreiben += `: ${auf}`;
    if (z.vektoren) schreiben += ` – Bedeutung für ${z.vektoren} ${z.vektoren === 1 ? "Teil" : "Teile"} gerechnet`;
  }
  return { lesen, schreiben };
}

/** Lage des Abschnitts „Gedächtnis" im Baum (aufgeklappt). */
export function zugriffeLage(st: GedaechtnisStand | undefined, worte: { laedt: string; fehler: string; bedeutung: (p: string) => string; stunde: (lesen: number, schreiben: number) => string; still: string }): LageTeil[] {
  if (!st) return [{ text: worte.laedt }];
  const teile: LageTeil[] = [];
  if (st.fehler) teile.push({ text: worte.fehler, ton: "danger" });
  if (st.offen > 0 && st.einbetten === "laeuft") teile.push({ text: worte.bedeutung(`${Math.floor(anteilBedeutung(st) * 100)} %`), ton: "accent" });
  const z = st.zugriffe;
  if (z && (z.lesen || z.schreiben)) {
    const e = zugriffeErklaerung(z);
    teile.push({ text: worte.stunde(z.lesen, z.schreiben), titel: `${e.lesen}\n${e.schreiben}` });
  }
  else teile.push({ text: worte.still });
  return teile;
}
